// Single source of truth for site copy, navigation, and contact details.
// Editing wording or links here should never require touching a component.
// Copy that changes with the language toggle is a `{ en, ja }` pair, resolved
// by `t()` from i18n/LanguageContext; plain strings (names, URLs) are shared.

// Real artifacts lifted from each project's own repo (logo/avatar as shipped),
// shown inside the work plates. Webpack hashes these into the bundle.
import tangoLogo from "../assets/work/lets-tango.png";
import lucyAvatar from "../assets/work/ask-lucy.png";
import callsLogo from "../assets/work/calls-from-the-past.png";
// Illustrated avatars. Jay's is the same artwork as the @itsjaydesu GitHub/X
// profiles; Riho's was supplied by Jay (2026-09-28).
import jayAvatar from "../assets/profile/itsjaydesu.png";
import rihoAvatar from "../assets/profile/riho.png";

// In Japanese mode on wide screens the tagline hangs vertically beside the
// mark; these are its columns, broken at phrase boundaries so no word splits.
const TAGLINE_JA_COLUMNS = [
  "新しいアイデアに、",
  "時間とエネルギーと資本を注ぐ。",
  "小さなクリエイティブ・ビルダーの",
  "集まりです。",
];

export const SITE = {
  name: "Digital Philosophy",
  legalName: "Digital Philosophy PTE. LTD.",
  // The studio works out of Tokyo; the entity is registered in Singapore.
  cities: { en: "Tokyo 東京 · Singapore", ja: "東京 · シンガポール" },
  address: "202A S Bridge Rd, Singapore 058751",
  email: "hello@digitalphilosophy.io",
  tagline: {
    en: "A small group of creative builders, who invest time, energy and capital into new ideas.",
    ja: TAGLINE_JA_COLUMNS.join(""),
  },
  taglineColumnsJa: TAGLINE_JA_COLUMNS,
  description:
    "An independent software studio in Tokyo and Singapore. We build a small number of things, slowly, where engineering meets craft.",
};

// Small UI strings that belong to no single section.
export const UI = {
  scroll: { en: "Scroll", ja: "スクロール" },
  language: { en: "Language", ja: "言語" },
  home: { en: "Digital Philosophy — back to top", ja: "Digital Philosophy — ページの先頭へ" },
  privacy: { en: "Privacy", ja: "プライバシー" },
};

// The language toggle's options, each labelled in its own language.
export const LANGUAGE_OPTIONS = [
  { id: "en", label: "EN" },
  { id: "ja", label: "日本語" },
];

// Sections tracked by the scroll-spy + right-edge scene indicator, top → bottom.
export const SCENES = [
  { id: "intro", label: "Intro" },
  { id: "statement", label: "Studio" },
  { id: "approach", label: "Approach" },
  { id: "contact", label: "Contact" },
];

export const SCENE_IDS = SCENES.map((s) => s.id);

export const STATEMENT = {
  eyebrow: "01 · Studio",
  heading:
    "We build a small number of things, slowly, where engineering meets craft.",
  body:
    "No feeds, no noise. Software as a quiet room — considered, precise, and built to last longer than a trend.",
};

export const APPROACH = {
  eyebrow: "02 · Approach",
  heading:
    "We prototype in code, listen closely, and ship slowly.\nThe details are the product.",
};

// Selected work shown on the homepage. `hue` is a muted per-project tint used
// as a whisper-level hover wash and the glow in the project's plate; `image`
// is the project's real artifact (its own logo/avatar, files under
// src/assets/work). `art` names the stroke-drawn emblem in
// components/home/ProjectArt.js, kept as the fallback for any item without an
// image; `href` may be null while a project has no public home yet (the row
// then renders without a link). `live` shows the status dot.
export const PROJECTS = {
  eyebrow: { en: "Work", ja: "プロジェクト" },
  liveLabel: { en: "Live", ja: "公開中" },
  items: [
    {
      name: "Lets Tango",
      medium: { en: "Web", ja: "ウェブ" },
      blurb: {
        en: "A Japanese communication assistant — translate, fix, and reply in words you'd actually send.",
        ja: "日本語コミュニケーションのアシスタント。翻訳も添削も返信も、実際に送れる言葉で。",
      },
      href: "https://lets-tango.com",
      // rose, matched to the pixel wordmark's pink rather than the old rust
      hue: "#c24d64",
      art: "tango",
      image: tangoLogo,
      live: true,
    },
    {
      name: "Ask Lucy",
      medium: { en: "iOS app", ja: "iOS アプリ" },
      blurb: {
        en: "A travel companion that reads menus, signs, and handwriting wherever you land.",
        ja: "メニューも看板も手書きの文字も。どこへ行っても読み解いてくれる旅のお供。",
      },
      href: "https://ask-lucy.ai",
      hue: "#7d74b8",
      art: "lucy",
      image: lucyAvatar,
      live: true,
    },
    {
      name: "Calls From The Past",
      medium: { en: "Web", ja: "ウェブ" },
      blurb: {
        en: "Pick up the phone and talk with Einstein, Cleopatra, or a Londoner from 1665.",
        ja: "受話器を取れば、アインシュタインやクレオパトラ、1665年のロンドン市民と話せます。",
      },
      href: "https://callsfromthepast.com",
      hue: "#a1854f",
      art: "calls",
      image: callsLogo,
      live: true,
    },
  ],
};

// Who's behind the studio. Jay's bio is distilled from the about page at
// itsjaydesu.com (jayfolio) — edit there first, then mirror the essentials.
// Each card shows the role in the current language with the other language
// beside it. `details` rows render as a label/value list under the bio.
export const TEAM = {
  eyebrow: { en: "Team", ja: "チーム" },
  members: [
    {
      name: "Jay Winder",
      role: { en: "Founder & CEO", ja: "創業者・CEO" },
      avatar: jayAvatar,
      avatarAlt: {
        en: "Jay Winder's illustrated avatar, a smiling figure tipping a white fedora",
        ja: "Jay Winder のイラストアバター。白いフェドーラ帽に手をかけて笑う人物",
      },
      bio: [
        {
          en: "A technologist who loves making things — software mostly, with detours into art, words, music, and comedy.",
          ja: "ものづくりを愛するテクノロジスト。主にソフトウェア、ときどきアート、文章、音楽、コメディにも寄り道します。",
        },
        {
          en: "Previously founded MakeLeaps, Japan's first cloud invoicing startup. Now building the projects above, and Silicon Dojo — a fund connecting Japanese investors to Silicon Valley.",
          ja: "日本初のクラウド請求書サービス MakeLeaps を創業。現在は上記のプロジェクトに加え、日本の投資家とシリコンバレーをつなぐファンド Silicon Dojo を手がけています。",
        },
      ],
      details: [
        { label: { en: "Languages", ja: "言語" }, value: "English · 日本語" },
        {
          label: { en: "Link", ja: "リンク" },
          value: "@itsjaydesu",
          href: "https://itsjaydesu.com",
        },
      ],
    },
    {
      name: "Riho Winder",
      role: { en: "COO", ja: "最高執行責任者" },
      avatar: rihoAvatar,
      avatarAlt: {
        en: "Riho Winder's illustrated avatar, smiling among green leaves",
        ja: "Riho Winder のイラストアバター。緑の葉の中で笑う人物",
      },
      bio: [
        {
          en: "A digital marketer and experienced startup operator who turns ideas into running businesses. Perfectly bilingual in Japanese and English.",
          ja: "アイデアを動き続ける事業に変えてきた、デジタルマーケターであり経験豊富なスタートアップ・オペレーター。日本語と英語の完全なバイリンガルです。",
        },
      ],
      details: [
        { label: { en: "Languages", ja: "言語" }, value: "English · 日本語" },
        {
          label: { en: "Off hours", ja: "オフの日" },
          value: {
            en: "Her dogs, and whatever is cooking",
            ja: "愛犬のお世話と、その日の料理",
          },
        },
      ],
    },
  ],
};

export const CONTACT = {
  eyebrow: { en: "Contact", ja: "お問い合わせ" },
};

export const SOCIAL = [
  // X / Twitter temporarily disabled — re-enable when ready:
  // { label: "X / Twitter", href: "https://x.com/YOUR_HANDLE", external: true },
  { label: "GitHub", href: "https://github.com/itsjaydesu", external: true },
];
